import { memo } from 'react';
import { Monitor } from 'lucide-react';
import { EmptyState } from '@/components/atoms/EmptyState';
import { useMediaQuery } from '@/hooks/useMediaQuery';

interface AdminEditorMobileGuardProps {
  children: React.ReactNode;
}

/**
 * Gate admin editor routes to ≥md (768px). The TipTap toolbar, sidebar forms,
 * and autosave status row are unusable on phones — rather than silently
 * render an unreadable view, show an explicit "use a larger screen" state.
 *
 * Uses `useMediaQuery` so the heavy editor tree (TipTap, lazy bundles,
 * autosave effects) does NOT mount on mobile. Bundle still downloads because
 * React Router lazy preloading runs independently, but the component tree
 * stays unmounted so no effects fire and no memory is held.
 *
 * Trade-off versus pure-CSS `hidden md:block`: one matchMedia listener per
 * route. Worth it to avoid spinning up autosave timers, RHF state, and
 * editor instances on a device that can't use them.
 */
export const AdminEditorMobileGuard = memo<AdminEditorMobileGuardProps>(({ children }) => {
  const isDesktop = useMediaQuery('(min-width: 768px)');

  if (!isDesktop) {
    return (
      <div className="h-full p-6 flex items-center">
        <EmptyState
          icon={Monitor}
          title="Please use a larger screen"
          description="The course editor needs at least a tablet-sized screen. Switch to a device that's 768px wide or larger to continue editing."
        />
      </div>
    );
  }

  return <>{children}</>;
});

AdminEditorMobileGuard.displayName = 'AdminEditorMobileGuard';
